import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { cn } from "@/lib/utils";
import { useAuth } from "../context/AuthContext";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import ModificaVociFatturaDialog from "../components/ModificaVociFatturaDialog";
import ConfermaSedeFicDialog from "../components/ConfermaSedeFicDialog";
import RecuperoCollegamentoFicDialog from "../components/RecuperoCollegamentoFicDialog";
import FatturazioneTabsContent from "../components/FatturazioneTabsContent";
import SdiErrorMessage from "../components/SdiErrorMessage";
import SdiTimeline from "../components/SdiTimeline";
import FatturaAllegatiSection from "../components/FatturaAllegatiSection";
import {
  SDI_STATI,
  computeSdiStats,
  filterFattureSdi,
  fatturaEliminabile,
  fatturaEliminaSuFic,
  fatturaModificabile,
  fatturaInCodaSdi,
} from "../utils/fatturazioneSdi";
import { FIC_RATE_LIMIT_MESSAGE } from "../utils/ficSyncStato";
import { currentMonthValue } from "../utils/fatturazioneControlloMensile";
import { toastSdiError } from "../utils/sdiErrorMessages";
import { isFicSedePropostaError, ficSedePropostaFromError, postInviaSdi } from "../utils/ficSedeInvio";
import { fatturaRecuperabileCollegamentoFic } from "../utils/ficRecuperoCollegamento";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  voceDescrizionePerUI,
  fatturaContenutoRiepilogo,
  labelDestinatarioOspite,
} from "../utils/fatturazioneLabels";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { computeUnifiedKpis, computeImportoFattureAttivePeriodo, computeDifferenzaAttivePassive } from "../utils/fatturazioneKpi";
import {
  Receipt,
  Loader2,
  RefreshCw,
  ExternalLink,
  Building2,
  FileText,
  Download,
  Send,
  ClipboardList,
  Trash2,
  Pencil,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
  Eye,
  Bell,
  Settings,
  ChevronDown,
  SlidersHorizontal,
  Cloud,
  Link2,
  Unlink,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATO_BADGE = {
  bozza: { label: "Generata", className: "bg-blue-100 text-blue-800" },
  generata: { label: "Da inviare", className: "bg-orange-100 text-orange-800" },
  approvata: { label: "Approvata", className: "bg-violet-100 text-violet-800" },
  in_coda_sdi: { label: "In coda SDI", className: "bg-sky-100 text-sky-800 ring-1 ring-sky-200" },
  inviata_sdi: { label: "Inviata", className: "bg-emerald-100 text-emerald-800" },
  consegnata: { label: "Inviata", className: "bg-emerald-100 text-emerald-800" },
  accettata: { label: "Inviata", className: "bg-emerald-100 text-emerald-800" },
  rifiutata: { label: "Annullata", className: "bg-slate-200 text-slate-600" },
  scartata: { label: "Annullata", className: "bg-slate-200 text-slate-600" },
  stornata: { label: "Stornata", className: "bg-purple-100 text-purple-800" },
};

const FILTRO_STATO_UI = [
  { value: "__tutti__", label: "Tutti gli stati" },
  { value: "generata", label: "Generata" },
  { value: "da_inviare", label: "Da inviare" },
  { value: "inviata", label: "Inviata" },
  { value: "annullata", label: "Annullata" },
];

function matchFiltroStatoUi(f, filtro) {
  if (!filtro || filtro === "__tutti__") return true;
  const s = f.stato;
  if (filtro === "generata") return s === "bozza";
  if (filtro === "da_inviare") return s === "generata";
  if (filtro === "inviata") return ["inviata_sdi", "consegnata", "accettata"].includes(s);
  if (filtro === "annullata") return ["rifiutata", "scartata", "stornata"].includes(s);
  return s === filtro;
}

function formatDataIt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT");
  } catch {
    return String(iso).slice(0, 10);
  }
}

function formatDataOraIt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
}

function formatEuro(n) {
  return `€ ${Number(n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

function fascicoloLabel(fascicoli, fascicoloId) {
  if (!fascicoloId) return null;
  const f = fascicoli.find((x) => x.id === fascicoloId);
  if (!f) return null;
  const fromAnag =
    [f.anagrafica?.cognome, f.anagrafica?.nome].filter(Boolean).join(" ").trim() ||
    f.anagrafica?.nome_completo?.trim() ||
    "";
  return f.nome_completo?.trim() || fromAnag || "Ospite";
}

const STATO_PAGAMENTO_PASSIVA_UI = {
  pagata: { label: "Pagata", className: "bg-[#e6f7ed] text-[#1a8754]" },
  non_pagata: { label: "Da pagare", className: "bg-red-50 text-red-700" },
  parziale: { label: "Parzialmente pagata", className: "bg-[#ffebd8] text-[#854d0e]" },
  scaduta: { label: "Scaduta", className: "bg-red-50 text-red-800" },
};

const METODO_PAGAMENTO_LABELS = {
  bonifico: "Bonifico",
  rid: "RID",
  carta: "Carta",
  contanti: "Contanti",
  altro: "Altro",
};

const CATEGORIE_PASSIVA = [
  "Alimentari",
  "Farmaci",
  "Utenze",
  "Personale",
  "Manutenzione",
  "Forniture",
  "Servizi",
  "Altro",
];

const TIPO_PASSIVA_LABELS = {
  expense: "Spese",
  passive_credit_note: "Nota credito",
  passive_delivery_note: "DDT",
  self_invoice: "Autofattura",
};

const REGIMI_IVA_LABELS = {
  esente_art10: "Esente Art.10",
  iva_4: "IVA 4%",
  iva_22: "IVA 22%",
  esclusa_art15: "Escl. Art.15",
};

const FATTURAZIONE_TABS = new Set([
  "controllo-mensile",
  "fatture-generate",
  "note-credito",
  "fatture-attive",
  "fatture-passive",
  "fatture-sdi",
]);

const MESI = [
  "", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function formatMese(mese) {
  if (!mese) return "";
  const [y, m] = mese.split("-");
  if (!y || !m) return mese;
  return `${MESI[parseInt(m, 10)] || m} ${y}`;
}

/** @deprecated use fatturaEliminabile from fatturazioneSdi */
function fatturaModificabileEliminabile(f) {
  return fatturaEliminabile(f);
}

function FatturaDetailBody({
  f,
  formatDataIt: fmtData,
  formatDataOraIt: fmtDataOra,
  headers,
  canEditAllegati,
  onAllegatiChanged,
  ospiteNome,
}) {
  const inCodaSdi = fatturaInCodaSdi(f);
  const stFatt = inCodaSdi
    ? STATO_BADGE.in_coda_sdi
    : STATO_BADGE[f.stato] || { label: f.stato, className: "bg-slate-100" };
  const stPag = STATO_PAGAMENTO_PASSIVA_UI[f.stato_pagamento] || STATO_PAGAMENTO_PASSIVA_UI.non_pagata;
  const riepilogo = f.riepilogo_iva || [];
  const isSdi = SDI_STATI.has(f.stato);
  const destLabel = labelDestinatarioOspite({
    destinatarioNome: f.destinatario_nome,
    ospiteNome: ospiteNome || "",
  });

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap gap-2">
        {f.tipo_documento === "nota_credito" && (
          <Badge className="bg-purple-100 text-purple-800">Nota di credito</Badge>
        )}
        <Badge className={cn(stFatt.className, "inline-flex items-center gap-1.5")}>
          {inCodaSdi && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />}
          {stFatt.label}
        </Badge>
        <Badge className={stPag.className}>{stPag.label}</Badge>
        {f.tipo_documento !== "nota_credito" && f.nota_credito_numero && (
          <Badge className="bg-purple-50 text-purple-800 border border-purple-200">
            NC {f.nota_credito_numero}
          </Badge>
        )}
        {f.ospite_trasferito && (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
            Ospite trasferito{f.trasferito_a_struttura_nome ? ` a ${f.trasferito_a_struttura_nome}` : ""}
          </Badge>
        )}
      </div>

      {inCodaSdi && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900 flex items-start gap-2">
          <Loader2 className="w-4 h-4 animate-spin shrink-0 mt-0.5" aria-hidden />
          <p>
            Invio allo SDI in corso in background. Attendi il completamento prima di riprovare:
            lo stato passerà a Inviata quando l&apos;operazione sarà conclusa.
          </p>
        </div>
      )}

      {isSdi && (
        <SdiTimeline fattura={f} formatDataIt={fmtData || formatDataIt} formatDataOraIt={fmtDataOra || formatDataOraIt} />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <span className="text-xs text-slate-500">Mese competenza</span>
          <p className="font-medium">{formatMese(f.mese)}</p>
        </div>
        <div>
          <span className="text-xs text-slate-500">Tipo documento</span>
          <p className="font-medium">{f.tipo_documento === "nota_credito" ? "Nota di credito" : "Fattura"}</p>
        </div>
        <div>
          <span className="text-xs text-slate-500">Data emissione</span>
          <p className="font-medium">
            {f.data_emissione ? new Date(f.data_emissione).toLocaleDateString("it-IT") : "—"}
          </p>
        </div>
        <div>
          <span className="text-xs text-slate-500">Scadenza</span>
          <p className="font-medium">
            {f.data_scadenza ? new Date(f.data_scadenza).toLocaleDateString("it-IT") : "—"}
          </p>
        </div>
        <div>
          <span className="text-xs text-slate-500">Importo pagato</span>
          <p className="font-medium text-emerald-600">
            € {(f.importo_pagato || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <span className="text-xs text-slate-500">Residuo</span>
          <p className="font-medium text-red-600">
            € {((f.totale || 0) - (f.importo_pagato || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
          </p>
        </div>
        {f.modalita_pagamento && (
          <div>
            <span className="text-xs text-slate-500">Modalità pagamento</span>
            <p className="font-medium font-mono text-xs">{f.modalita_pagamento}</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-1">
        <p className="text-xs font-semibold text-slate-600">Destinatario</p>
        <p className="font-medium">{destLabel.primary}</p>
        {destLabel.secondary && (
          <p className="text-sm text-slate-600">Ospite: {destLabel.secondary}</p>
        )}
        {f.destinatario_cf && (
          <p className="text-xs text-slate-600">CF: {f.destinatario_cf}</p>
        )}
        {f.destinatario_pec && (
          <p className="text-xs text-slate-600">PEC: {f.destinatario_pec}</p>
        )}
        {f.destinatario_sdi && (
          <p className="text-xs text-slate-600">Codice SDI: {f.destinatario_sdi}</p>
        )}
      </div>

      {(f.provider_id || f.sdi_invio_at || f.ultimo_errore_sdi) && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-1 text-xs">
          <p className="font-semibold text-slate-600">SDI</p>
          {f.provider_id && <p>ID provider: <span className="font-mono">{f.provider_id}</span></p>}
          {f.sdi_invio_at && (
            <p>Invio: {new Date(f.sdi_invio_at).toLocaleString("it-IT")}</p>
          )}
          {f.ultimo_errore_sdi && (
            <SdiErrorMessage
              error={f.ultimo_errore_sdi}
              userMessage={f.ultimo_errore_sdi_leggibile}
              title="Ultimo errore SDI"
              compact
              className="mt-2"
            />
          )}
        </div>
      )}

      {f.voci && f.voci.length > 0 && (
        <div>
          <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-2">
            <ClipboardList className="w-4 h-4 text-slate-500" />
            Voci
          </h4>
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            {f.voci.map((voce, idx) => {
              const regime = REGIMI_IVA_LABELS[voce.regime_iva] || voce.regime_iva;
              return (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex-1 min-w-0 pr-2">
                    <span className="text-sm">{voceDescrizionePerUI(voce)}</span>
                    {voce.quantita > 1 && (
                      <span className="text-xs text-slate-400 ml-2">×{voce.quantita}</span>
                    )}
                    <p className="text-[10px] text-slate-400">{regime}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-medium">
                      € {(voce.quantita * voce.prezzo_unitario).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {f.id && headers && (
        <FatturaAllegatiSection
          fatturaId={f.id}
          headers={headers}
          canEdit={canEditAllegati}
          onChanged={onAllegatiChanged}
        />
      )}

      {riepilogo.length > 0 && (
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="font-semibold text-slate-700 mb-2">Riepilogo IVA</p>
          <div className="grid grid-cols-4 gap-2 text-xs text-slate-500 border-b pb-1 mb-1">
            <span>Aliquota</span>
            <span className="text-right">Imponibile</span>
            <span className="text-right">Imposta</span>
            <span className="text-right">Totale</span>
          </div>
          {riepilogo.map((r, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 py-1 border-b border-slate-100 last:border-0 text-sm">
              <span className="text-slate-600">{r.label}</span>
              <span className="text-right">€ {Number(r.imponibile).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
              <span className="text-right text-amber-600">
                {r.imposta > 0 ? `€ ${Number(r.imposta).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}
              </span>
              <span className="text-right font-medium">
                € {Number(r.totale).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </span>
            </div>
          ))}
          {f.bollo > 0 && (
            <div className="grid grid-cols-4 gap-2 text-sm py-1 mt-1">
              <span className="text-slate-600">Bollo</span>
              <span className="text-right">—</span>
              <span className="text-right">—</span>
              <span className="text-right font-medium">
                € {Number(f.bollo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-2 border-t-2 border-emerald-200 font-bold text-base">
        <span>Totale fattura</span>
        <span className="text-emerald-700">
          € {(f.totale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </span>
      </div>

      {f.pagamenti && f.pagamenti.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1">Pagamenti registrati</p>
          <ul className="space-y-1 text-xs text-slate-600">
            {f.pagamenti.map((p, pi) => (
              <li key={pi} className="flex justify-between border-b border-slate-100 pb-1">
                <span>
                  {p.data_pagamento ? new Date(p.data_pagamento).toLocaleDateString("it-IT") : "—"} · {p.metodo || "—"}
                </span>
                <span className="font-medium text-emerald-600">
                  € {Number(p.importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {f.solleciti && f.solleciti.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1">Solleciti</p>
          <ul className="space-y-1 text-xs text-slate-600">
            {f.solleciti.map((s, si) => (
              <li key={si} className="flex justify-between">
                <span>
                  {s.data ? new Date(s.data).toLocaleDateString("it-IT") : "—"} · {s.email_destinatario || "—"}
                </span>
                <Badge className={s.inviato ? "bg-emerald-100 text-emerald-800 text-[10px]" : "bg-slate-100 text-slate-700 text-[10px]"}>
                  {s.inviato ? "Inviato" : "Registrato"}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Fatturazione() {
  const { user, token, activeStruttura } = useAuth();
  const navigate = useNavigate();
  const [fascicoli, setFascicoli] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [loadingFascicoli, setLoadingFascicoli] = useState(true);
  const [loadingFatture, setLoadingFatture] = useState(true);
  const [filtroFascicolo, setFiltroFascicolo] = useState("__tutti__");
  const [filtroMese, setFiltroMese] = useState("__tutti__");
  const [filtroStato, setFiltroStato] = useState("__tutti__");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailFattura, setDetailFattura] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailDirtyRef = useRef(false);
  const controlloFullRefreshRef = useRef(null);
  const pendingFatturaRefreshRef = useRef(new Map());
  const [sendingSdi, setSendingSdi] = useState(false);
  const [approvingBozza, setApprovingBozza] = useState(false);
  const [savingFatturaPatch, setSavingFatturaPatch] = useState(false);
  const [creatingNotaCredito, setCreatingNotaCredito] = useState(null);
  const [ncSearchQuery, setNcSearchQuery] = useState("");
  const [ncFiltroMese, setNcFiltroMese] = useState("__tutti__");
  const [rigeneratingFattura, setRigeneratingFattura] = useState(null);
  const [deletingFattura, setDeletingFattura] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState(null);
  const [editVociDialogOpen, setEditVociDialogOpen] = useState(false);
  const [fattConfig, setFattConfig] = useState({});

  const [openingDetailPdf, setOpeningDetailPdf] = useState(false);

  const [showSollecitoDialog, setShowSollecitoDialog] = useState(false);
  const [sollecitoForm, setSollecitoForm] = useState({ messaggio: "", email_destinatario: "" });
  const [sendingSollecito, setSendingSollecito] = useState(false);
  const [ficSedeConfirm, setFicSedeConfirm] = useState(null);
  const [confirmingFicSede, setConfirmingFicSede] = useState(false);
  const [recuperoFicOpen, setRecuperoFicOpen] = useState(false);
  const [recuperoFicFattura, setRecuperoFicFattura] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabFromUrl && FATTURAZIONE_TABS.has(tabFromUrl) ? tabFromUrl : "controllo-mensile",
  );

  // Ex tab Configurazione: reindirizza alle pagine dedicate
  useEffect(() => {
    if (tabFromUrl !== "configurazione") return;
    const section = searchParams.get("section");
    navigate(section === "aspetto" ? "/fatturazione/aspetto" : "/fatturazione/modelli", { replace: true });
  }, [tabFromUrl, searchParams, navigate]);

  useEffect(() => {
    if (tabFromUrl && FATTURAZIONE_TABS.has(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      const next = new URLSearchParams(searchParams);
      if (tab === "controllo-mensile") {
        next.delete("tab");
      } else {
        next.set("tab", tab);
      }
      next.delete("section");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const [meseControllo, setMeseControllo] = useState(currentMonthValue);
  const [controlloData, setControlloData] = useState(null);
  const [controlloLoading, setControlloLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [archivioFiltroPagamento, setArchivioFiltroPagamento] = useState("__tutti__");
  const [archivioFiltroMese, setArchivioFiltroMese] = useState(currentMonthValue);
  const [archivioSearchQuery, setArchivioSearchQuery] = useState("");
  const [archivioStats, setArchivioStats] = useState(null);
  const [archivioStatsLoading, setArchivioStatsLoading] = useState(false);
  const [passivePayTarget, setPassivePayTarget] = useState(null);
  const [passivePayForm, setPassivePayForm] = useState({
    stato_pagamento: "pagata",
    data_pagamento: "",
    metodo_pagamento: "bonifico",
    importo_pagato: "",
    data_scadenza: "",
    centro_costo: "",
    categoria: "",
  });
  const [savingPassivePay, setSavingPassivePay] = useState(false);

  // ---- Archivio fatture passive (DB locale) ----
  const [archivioItems, setArchivioItems] = useState([]);
  const [archivioLoading, setArchivioLoading] = useState(false);
  const [archivioPage, setArchivioPage] = useState(1);
  const [archivioLastPage, setArchivioLastPage] = useState(1);
  const [archivioTotal, setArchivioTotal] = useState(0);
  const [archivioDataDa, setArchivioDataDa] = useState("");
  const [archivioDataA, setArchivioDataA] = useState("");
  const [archivioFornitore, setArchivioFornitore] = useState("");
  const [archivioLoaded, setArchivioLoaded] = useState(false);
  const [downloadingArchivio, setDownloadingArchivio] = useState(null);

  // ---- Sincronizzazione manuale ----
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncDataDa, setSyncDataDa] = useState("");
  const [syncDataA, setSyncDataA] = useState("");
  const [syncTipoFic, setSyncTipoFic] = useState("expense");

  // ---- Live search dal provider (legacy) ----
  const [passiveItems, setPassiveItems] = useState([]);
  const [passiveLoading, setPassiveLoading] = useState(false);
  const [passivePage, setPassivePage] = useState(1);
  const [passiveLastPage, setPassiveLastPage] = useState(1);
  const [passiveTotal, setPassiveTotal] = useState(0);
  const [passiveMessage, setPassiveMessage] = useState("");
  const [passiveDataDa, setPassiveDataDa] = useState("");
  const [passiveDataA, setPassiveDataA] = useState("");
  const [passiveTipoFic, setPassiveTipoFic] = useState("expense");
  const [downloadingPassive, setDownloadingPassive] = useState(null);
  const [passiveSearched, setPassiveSearched] = useState(false);

  const [attiveFiltroMese, setAttiveFiltroMese] = useState(currentMonthValue);
  const [attiveItems, setAttiveItems] = useState([]);
  const [attiveLoading, setAttiveLoading] = useState(false);
  const [attivePage, setAttivePage] = useState(1);
  const [attiveLastPage, setAttiveLastPage] = useState(1);
  const [attiveTotal, setAttiveTotal] = useState(0);
  const [attiveSearchQuery, setAttiveSearchQuery] = useState("");
  const [attiveStats, setAttiveStats] = useState(null);
  const [downloadingAttivaPdf, setDownloadingAttivaPdf] = useState(null);
  const [deleteAttivaTarget, setDeleteAttivaTarget] = useState(null);
  const [deletingAttivaFic, setDeletingAttivaFic] = useState(false);
  const [syncFicNcLoading, setSyncFicNcLoading] = useState(false);
  const [syncPagamentiLoading, setSyncPagamentiLoading] = useState(false);

  const [sdiSearchQuery, setSdiSearchQuery] = useState("");
  const [sdiFiltroStato, setSdiFiltroStato] = useState("__tutti__");
  const [sdiFiltroMese, setSdiFiltroMese] = useState("__tutti__");
  const [sdiDataDa, setSdiDataDa] = useState("");
  const [sdiDataA, setSdiDataA] = useState("");
  const [sdiPage, setSdiPage] = useState(1);
  const [sdiRowsPerPage, setSdiRowsPerPage] = useState(10);
  const [sdiSyncLoading, setSdiSyncLoading] = useState(false);
  const [sdiUsageLimitBanner, setSdiUsageLimitBanner] = useState(null);

  const emailOk = canAccessFatturazioneElettronica(user);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadFicSyncState = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fic-sync-stato`, { headers });
      if (res.data?.sospeso) {
        setSdiUsageLimitBanner({
          ...res.data,
          message: FIC_RATE_LIMIT_MESSAGE,
        });
      } else {
        setSdiUsageLimitBanner(null);
      }
    } catch {
      // Stato aggiuntivo: la pagina continua a usare i dati locali.
    }
  }, [headers, token]);

  useEffect(() => {
    loadFicSyncState();
  }, [loadFicSyncState]);

  const strutturaIdForConfig = useMemo(
    () => activeStruttura?.id || activeStruttura?.zohoRecordId || user?.struttura_id || user?.zohoRecordId || null,
    [activeStruttura?.id, activeStruttura?.zohoRecordId, user?.struttura_id, user?.zohoRecordId],
  );

  const hasProviderConfig = !!(fattConfig && fattConfig.provider);
  const canInviaSdi = hasProviderConfig && !!fattConfig.abilitato;

  const detailProblemiControllo = useMemo(() => {
    if (!detailId || !controlloData?.righe) return [];
    const row = controlloData.righe.find((r) => r.fattura_id === detailId);
    return row?.problemi || [];
  }, [detailId, controlloData]);
  const isAruba = fattConfig.provider === "aruba";
  const isFic = fattConfig.provider === "fatture_in_cloud";

  const mesiOptions = useMemo(() => {
    const out = [];
    const d = new Date();
    for (let i = 0; i < 24; i++) {
      const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
      const val = `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
      out.push(val);
    }
    return out;
  }, []);

  const loadFascicoli = useCallback(async () => {
    setLoadingFascicoli(true);
    try {
      const res = await axios.get(`${API_URL}/api/fascicoli-assistenziali`, { headers });
      setFascicoli(res.data || []);
    } catch {
      setFascicoli([]);
    } finally {
      setLoadingFascicoli(false);
    }
  }, [headers, activeStruttura?.id]);

  const loadFatture = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingFatture(true);
    try {
      const params = {};
      if (filtroFascicolo && filtroFascicolo !== "__tutti__") {
        params.fascicolo_id = filtroFascicolo;
      }
      if (filtroMese && filtroMese !== "__tutti__") {
        params.mese = filtroMese;
      }
      const res = await axios.get(`${API_URL}/api/fatturazione/elenco-fatture`, { headers, params });
      const incoming = res.data.fatture || [];
      const pendingMap = pendingFatturaRefreshRef.current;
      const merged = incoming.map((f) => {
        const pending = pendingMap.get(f.id);
        if (!pending || Date.now() - pending.ts > 60_000) return f;
        const pendingStato = pending.patch?.stato;
        if (pendingStato && f.stato !== pendingStato && f.stato === "approvata") {
          return { ...f, ...pending.patch };
        }
        if (pendingStato && f.stato === pendingStato) {
          pendingMap.delete(f.id);
        }
        return f;
      });
      setFatture(merged);
    } catch {
      if (!silent) setFatture([]);
    } finally {
      if (!silent) setLoadingFatture(false);
    }
  }, [headers, filtroFascicolo, filtroMese, activeStruttura?.id]);

  const loadControllo = useCallback(async ({ silent = false } = {}) => {
    if (!meseControllo) return;
    if (!silent) setControlloLoading(true);
    try {
      const params = { mese: meseControllo };
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/controllo-mensile`, {
        headers,
        params,
      });
      setControlloData(res.data);
    } catch {
      if (!silent) setControlloData(null);
    } finally {
      if (!silent) setControlloLoading(false);
    }
  }, [headers, meseControllo, strutturaIdForConfig]);

  const registerControlloFullRefresh = useCallback((fn) => {
    controlloFullRefreshRef.current = fn;
  }, []);

  const reloadControlloCompleto = useCallback(async () => {
    if (controlloFullRefreshRef.current) {
      await controlloFullRefreshRef.current();
      return;
    }
    await loadControllo();
  }, [loadControllo]);

  const patchControlloRigaFattura = useCallback((fatturaId, patch) => {
    if (!fatturaId || !patch || !Object.keys(patch).length) return;
    setControlloData((prev) => {
      if (!prev?.righe?.length) return prev;
      let changed = false;
      const righe = prev.righe.map((r) => {
        if (r.fattura_id !== fatturaId) return r;
        changed = true;
        return { ...r, ...patch };
      });
      if (!changed) return prev;
      return { ...prev, righe };
    });
  }, []);

  const refreshListsOnDetailClose = useCallback(
    async (fatturaId) => {
      await Promise.all([loadFatture({ silent: true }), reloadControlloCompleto()]);
      if (fatturaId) {
        pendingFatturaRefreshRef.current.delete(fatturaId);
      }
    },
    [loadFatture, reloadControlloCompleto],
  );

  const loadFattureAttive = useCallback(
    async (page = 1) => {
      if (!isFic || !attiveFiltroMese) return;
      setAttiveLoading(true);
      try {
        const params = { mese: attiveFiltroMese, page, per_page: 50 };
        if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
        const res = await axios.get(`${API_URL}/api/fatturazione/fatture-attive`, { headers, params });
        setAttiveItems(res.data?.items || []);
        setAttivePage(res.data?.page || page);
        setAttiveLastPage(res.data?.last_page || 1);
        setAttiveTotal(res.data?.total || 0);
        setAttiveStats({
          totale: res.data?.total || 0,
          importoTotale: res.data?.importo_totale ?? (res.data?.items || []).reduce((a, it) => a + (Number(it.totale) || 0), 0),
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || e.response?.data?.message || "Errore caricamento fatture attive FIC");
        setAttiveItems([]);
        setAttiveStats(null);
      } finally {
        setAttiveLoading(false);
      }
    },
    [headers, attiveFiltroMese, strutturaIdForConfig, isFic],
  );

  const handleSyncFicNoteCredito = useCallback(async () => {
    if (!isFic) return;
    setSyncFicNcLoading(true);
    try {
      const meseRef =
        (ncFiltroMese && ncFiltroMese !== "__tutti__" ? ncFiltroMese : null) ||
        attiveFiltroMese ||
        meseControllo;
      const params = {};
      if (meseRef) params.mese = meseRef;
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.post(`${API_URL}/api/fatturazione/sync-fic-note-credito`, {}, { headers, params });
      toast.success(res.data?.message || "Note di credito FIC sincronizzate");
      if (activeTab === "fatture-attive") await loadFattureAttive(attivePage);
      await loadFatture();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync note di credito FIC non riuscita");
    } finally {
      setSyncFicNcLoading(false);
    }
  }, [
    headers,
    isFic,
    ncFiltroMese,
    attiveFiltroMese,
    meseControllo,
    strutturaIdForConfig,
    loadFattureAttive,
    attivePage,
    loadFatture,
    activeTab,
  ]);

  const handleSyncPagamentiFic = useCallback(async () => {
    if (!isFic) return;
    setSyncPagamentiLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/fatturazione/sync-pagamenti-fic`, {}, { headers });
      toast.success(res.data?.message || "Stato pagamenti aggiornato da Fatture in Cloud");
      await loadFatture();
      if (activeTab === "fatture-attive") await loadFattureAttive(attivePage);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sincronizzazione pagamenti FIC non riuscita");
    } finally {
      setSyncPagamentiLoading(false);
    }
  }, [headers, isFic, loadFatture, activeTab, attivePage, loadFattureAttive]);

  useEffect(() => {
    if (activeTab === "fatture-attive" && isFic) {
      loadFattureAttive(1);
    }
  }, [activeTab, attiveFiltroMese, isFic, loadFattureAttive]);

  const currentYear = new Date().getFullYear();

  const fascicoliIdsSet = useMemo(() => {
    const s = new Set();
    for (const f of fascicoli) {
      if (f?.id) s.add(String(f.id));
      if (f?._id) s.add(String(f._id));
    }
    return s;
  }, [fascicoli]);

  /** L'API elenco fatture può restituire tutta l'org: limitiamo alla struttura attiva. */
  const fatturePerStruttura = useMemo(() => {
    if (!fascicoli.length && !strutturaIdForConfig) return fatture;
    const activeOrg = strutturaIdForConfig ? String(strutturaIdForConfig) : null;
    return fatture.filter((f) => {
      if (f.fascicolo_id) {
        // Match diretto id fascicolo (forma corta o ObjectId)
        if (fascicoliIdsSet.has(String(f.fascicolo_id))) return true;
        // ID salvato in forma diversa: non nascondere se è della struttura attiva
        // (caso Spoto/Terreni: collegata dal fascicolo ma assente in modulo)
        if (activeOrg && f.organizationId != null && String(f.organizationId) === activeOrg) {
          return true;
        }
        return false;
      }
      if (activeOrg && f.organizationId != null) {
        return String(f.organizationId) === activeOrg;
      }
      return true;
    });
  }, [fatture, fascicoli, fascicoliIdsSet, strutturaIdForConfig]);

  const fattureInAnno = useMemo(() => {
    return fatturePerStruttura.filter((f) => {
      const ref = f.data_emissione || (f.mese ? `${f.mese}-01` : null);
      if (!ref) return true;
      return String(ref).startsWith(String(currentYear));
    });
  }, [fatturePerStruttura, currentYear]);

  const fattureGenerateStats = useMemo(() => {
    const active = fattureInAnno.filter(
      (f) => f.tipo_documento !== "nota_credito" && !["rifiutata", "scartata"].includes(f.stato),
    );
    return {
      count: active.length,
      total: active.reduce((a, f) => a + (f.totale || 0), 0),
    };
  }, [fattureInAnno]);

  const sdiStats = useMemo(() => computeSdiStats(fattureInAnno), [fattureInAnno]);

  const fattureSdiStats = useMemo(
    () => ({ count: sdiStats.totale, total: sdiStats.importoTotale }),
    [sdiStats],
  );

  const unifiedKpis = useMemo(
    () =>
      computeUnifiedKpis({
        activeTab,
        controlloKpi: controlloData?.kpi,
        fatturePerStruttura,
        meseControllo,
        filtroMese:
          activeTab === "note-credito"
            ? ncFiltroMese
            : activeTab === "fatture-passive"
              ? archivioFiltroMese
              : filtroMese,
        sdiStats,
        archivioStats,
        attiveStats,
        loading: controlloLoading && activeTab === "controllo-mensile",
      }),
    [
      activeTab,
      controlloData?.kpi,
      fatturePerStruttura,
      meseControllo,
      filtroMese,
      ncFiltroMese,
      archivioFiltroMese,
      sdiStats,
      archivioStats,
      attiveStats,
      controlloLoading,
    ],
  );

  /** KPI confronto attive/passive/differenza sul mese (o anno se filtro «tutti»). */
  const kpiConfrontoMensile = useMemo(() => {
    const meseKpi =
      activeTab === "fatture-attive"
        ? attiveFiltroMese
        : archivioFiltroMese && archivioFiltroMese !== "__tutti__"
          ? archivioFiltroMese
          : archivioStats?.mese || null;
    const usaMese = !!meseKpi;
    const attiveAffidya = computeImportoFattureAttivePeriodo(fatturePerStruttura, {
      mese: usaMese ? meseKpi : null,
      anno: usaMese ? null : currentYear,
    });
    // Su tab Attive preferisci totale FIC del mese se disponibile
    const attiveImporto =
      activeTab === "fatture-attive" && attiveStats?.importoTotale != null
        ? Number(attiveStats.importoTotale) || 0
        : attiveAffidya.importo;
    const attiveCount =
      activeTab === "fatture-attive" && attiveStats?.totale != null
        ? Number(attiveStats.totale) || 0
        : attiveAffidya.count;
    const passiveImporto = Number(
      archivioStats?.importo_totale ??
        archivioStats?.importo_totale_mese ??
        archivioStats?.importo_totale_anno ??
        0,
    );
    const passiveCount = Number(archivioStats?.totale_fatture ?? 0);
    const { differenza } = computeDifferenzaAttivePassive(attiveImporto, passiveImporto);
    return {
      mese: meseKpi,
      periodo: usaMese || archivioStats?.periodo === "mese" ? "mese" : "anno",
      anno: archivioStats?.anno || currentYear,
      attive: { count: attiveCount, importo: attiveImporto },
      passive: { count: passiveCount, importo: passiveImporto },
      differenza,
      da_pagare: archivioStats?.da_pagare || null,
      scadute: archivioStats?.scadute || null,
      pagate: archivioStats?.pagate || null,
      loading: archivioStatsLoading || (activeTab === "fatture-attive" && attiveLoading),
    };
  }, [
    activeTab,
    attiveFiltroMese,
    archivioFiltroMese,
    archivioStats,
    attiveStats,
    attiveLoading,
    archivioStatsLoading,
    fatturePerStruttura,
    currentYear,
  ]);

  const fattureFiltrate = useMemo(() => {
    let list = [...fatturePerStruttura].filter((f) => f.tipo_documento !== "nota_credito");
    list = list.filter((f) => matchFiltroStatoUi(f, filtroStato));
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (f) =>
          (f.numero || "").toLowerCase().includes(q) ||
          (f.destinatario_nome || "").toLowerCase().includes(q) ||
          (fascicoloLabel(fascicoli, f.fascicolo_id) || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [fatturePerStruttura, filtroStato, searchQuery, fascicoli]);

  const noteCredito = useMemo(
    () => fatturePerStruttura.filter((f) => f.tipo_documento === "nota_credito"),
    [fatturePerStruttura],
  );

  const fattureEligibiliNc = useMemo(
    () =>
      fatturePerStruttura.filter(
        (f) =>
          f.tipo_documento !== "nota_credito" &&
          ["inviata_sdi", "consegnata", "accettata"].includes(f.stato) &&
          !f.nota_credito_id,
      ),
    [fatturePerStruttura],
  );

  const sdiMesiOptions = mesiOptions;

  const fattureSdiFiltrate = useMemo(
    () =>
      filterFattureSdi(fatturePerStruttura, {
        searchQuery: sdiSearchQuery,
        filtroStato: sdiFiltroStato,
        filtroMese: sdiFiltroMese,
        dataDa: sdiDataDa,
        dataA: sdiDataA,
        resolveOspite: (f) => fascicoloLabel(fascicoli, f.fascicolo_id) || "",
      }),
    [fatturePerStruttura, sdiSearchQuery, sdiFiltroStato, sdiFiltroMese, sdiDataDa, sdiDataA, fascicoli],
  );

  const sdiLastPage = Math.max(1, Math.ceil(fattureSdiFiltrate.length / sdiRowsPerPage) || 1);

  const fattureSdiPaginate = useMemo(() => {
    const start = (sdiPage - 1) * sdiRowsPerPage;
    return fattureSdiFiltrate.slice(start, start + sdiRowsPerPage);
  }, [fattureSdiFiltrate, sdiPage, sdiRowsPerPage]);

  useEffect(() => {
    setSdiPage(1);
  }, [sdiSearchQuery, sdiFiltroStato, sdiFiltroMese, sdiDataDa, sdiDataA, sdiRowsPerPage]);

  useEffect(() => {
    if (sdiPage > sdiLastPage) setSdiPage(sdiLastPage);
  }, [sdiPage, sdiLastPage]);

  const clearSdiFilters = useCallback(() => {
    setSdiSearchQuery("");
    setSdiFiltroStato("__tutti__");
    setSdiFiltroMese("__tutti__");
    setSdiDataDa("");
    setSdiDataA("");
  }, []);

  const archivioMesiOptions = useMemo(() => {
    const mesi = [];
    const now = new Date();
    for (let i = 0; i < 24; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      mesi.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return mesi;
  }, []);

  const tableLastPage = Math.max(1, Math.ceil(fattureFiltrate.length / rowsPerPage) || 1);

  const fatturePaginate = useMemo(() => {
    const start = (tablePage - 1) * rowsPerPage;
    return fattureFiltrate.slice(start, start + rowsPerPage);
  }, [fattureFiltrate, tablePage, rowsPerPage]);

  useEffect(() => {
    setTablePage(1);
  }, [filtroStato, filtroMese, filtroFascicolo, searchQuery, rowsPerPage]);

  useEffect(() => {
    if (tablePage > tableLastPage) setTablePage(tableLastPage);
  }, [tablePage, tableLastPage]);

  const handleSavePagamentoPassiva = async () => {
    if (!passivePayTarget?.id) return;
    setSavingPassivePay(true);
    try {
      const payload = {
        stato_pagamento: passivePayForm.stato_pagamento,
        data_scadenza: passivePayForm.data_scadenza || null,
      };
      if (["pagata", "parziale"].includes(passivePayForm.stato_pagamento)) {
        payload.data_pagamento = passivePayForm.data_pagamento;
        payload.metodo_pagamento = passivePayForm.metodo_pagamento;
      }
      if (passivePayForm.stato_pagamento === "parziale") {
        payload.importo_pagato = Number(passivePayForm.importo_pagato);
      }
      await axios.patch(
        `${API_URL}/api/fatturazione/fatture-passive/archivio/${passivePayTarget.id}/pagamento`,
        payload,
        { headers },
      );
      const metaOk = await handleSavePassivaMeta();
      toast.success(metaOk ? "Fattura passiva aggiornata" : "Pagamento salvato; verifica categoria/centro costo");
      setPassivePayTarget(null);
      loadArchivioPassive(archivioPage);
    } catch (e) {
      if (!e.response) return;
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingPassivePay(false);
    }
  };

  const handleOpenArchivioPdf = async (item) => {
    const key = `${item.id}:pdf-view`;
    setDownloadingArchivio(key);
    try {
      const params = {};
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture-passive/archivio/${item.id}/pdf`, {
        headers,
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anteprima PDF non disponibile");
    } finally {
      setDownloadingArchivio(null);
    }
  };

  const handleOpenAttivaPdf = async (item) => {
    const ficId = item?.fic_document_id;
    if (!ficId) return;
    const key = `${ficId}:pdf`;
    setDownloadingAttivaPdf(key);
    try {
      const params = {};
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture-attive/${ficId}/pdf`, {
        headers,
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anteprima PDF non disponibile su FIC");
    } finally {
      setDownloadingAttivaPdf(null);
    }
  };

  const openPassivePayDialog = (row) => {
    setPassivePayTarget(row);
    const display = row.stato_pagamento_display || row.stato_pagamento || "non_pagata";
    setPassivePayForm({
      stato_pagamento: display === "scaduta" ? "scaduta" : row.stato_pagamento || display || "non_pagata",
      data_pagamento: row.data_pagamento ? String(row.data_pagamento).slice(0, 10) : new Date().toISOString().slice(0, 10),
      metodo_pagamento: row.metodo_pagamento || "bonifico",
      importo_pagato: row.importo_pagato != null ? String(row.importo_pagato) : "",
      data_scadenza: row.data_scadenza ? String(row.data_scadenza).slice(0, 10) : "",
      centro_costo: row.centro_costo || "",
      categoria: row.categoria || "",
    });
  };

  const handleSavePassivaMeta = async () => {
    if (!passivePayTarget?.id) return true;
    try {
      await axios.patch(
        `${API_URL}/api/fatturazione/fatture-passive/archivio/${passivePayTarget.id}`,
        {
          centro_costo: passivePayForm.centro_costo || null,
          categoria: passivePayForm.categoria || null,
        },
        { headers },
      );
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio categorie non riuscito");
      return false;
    }
  };

  const loadFatturazioneConfig = useCallback(async () => {
    try {
      const params = strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {};
      const res = await axios.get(`${API_URL}/api/fatturazione/config`, { headers, params });
      setFattConfig(res.data.config || {});
    } catch {
      setFattConfig({});
    }
  }, [headers, strutturaIdForConfig]);

  const loadArchivioStats = useCallback(async () => {
    setArchivioStatsLoading(true);
    try {
      const meseRef =
        activeTab === "fatture-attive"
          ? attiveFiltroMese
          : archivioFiltroMese && archivioFiltroMese !== "__tutti__"
            ? archivioFiltroMese
            : null;
      const params = {};
      if (meseRef) {
        params.mese = meseRef;
        params.anno = Number(String(meseRef).slice(0, 4)) || currentYear;
      } else {
        params.anno = currentYear;
      }
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture-passive/archivio/stats`, { headers, params });
      setArchivioStats(res.data);
    } catch {
      setArchivioStats(null);
    } finally {
      setArchivioStatsLoading(false);
    }
  }, [
    headers,
    currentYear,
    strutturaIdForConfig,
    activeTab,
    attiveFiltroMese,
    archivioFiltroMese,
  ]);

  const loadArchivioPassive = useCallback(
    async (pageNum) => {
      const page = pageNum ?? archivioPage;
      setArchivioLoading(true);
      try {
        const params = { page, per_page: 50 };
        if (archivioDataDa) params.data_da = archivioDataDa;
        if (archivioDataA) params.data_a = archivioDataA;
        const q = archivioSearchQuery.trim() || archivioFornitore.trim();
        if (q) params.fornitore = q;
        if (archivioFiltroPagamento !== "__tutti__") params.stato_pagamento = archivioFiltroPagamento;
        if (archivioFiltroMese !== "__tutti__") params.mese = archivioFiltroMese;
        if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
        const res = await axios.get(`${API_URL}/api/fatturazione/fatture-passive/archivio`, { headers, params });
        setArchivioPage(page);
        setArchivioItems(res.data.items || []);
        setArchivioLastPage(res.data.last_page || 1);
        setArchivioTotal(res.data.total ?? 0);
        setArchivioLoaded(true);
        loadArchivioStats();
      } catch (e) {
        setArchivioItems([]);
        toast.error(e.response?.data?.detail || "Errore caricamento archivio fatture passive");
      } finally {
        setArchivioLoading(false);
      }
    },
    [
      headers,
      archivioPage,
      archivioDataDa,
      archivioDataA,
      archivioFornitore,
      archivioSearchQuery,
      archivioFiltroPagamento,
      archivioFiltroMese,
      strutturaIdForConfig,
      loadArchivioStats,
    ],
  );

  const handleSincronizzaPassive = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const params = { tipo_fic: syncTipoFic };
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      if (syncDataDa) params.data_da = syncDataDa;
      if (syncDataA) params.data_a = syncDataA;
      const res = await axios.post(`${API_URL}/api/fatturazione/fatture-passive/sincronizza`, null, { headers, params });
      setSyncResult(res.data);
      const inserted = res.data.inserted || 0;
      const updatedPay = res.data.updated_payments || 0;
      if (inserted > 0 || updatedPay > 0) {
        const parts = [];
        if (inserted > 0) parts.push(`${inserted} nuov${inserted === 1 ? "a" : "e"}`);
        if (updatedPay > 0) parts.push(`${updatedPay} stati pagamento aggiornati`);
        toast.success(`Sincronizzazione: ${parts.join(", ")}`);
        loadArchivioPassive(1);
      } else {
        toast.success(res.data.message || "Sincronizzazione completata — nessuna novità");
        loadArchivioPassive(1);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDownloadArchivio = async (item, formato = "xml") => {
    const key = `${item.id}:${formato}`;
    setDownloadingArchivio(key);
    try {
      const params = {};
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture-passive/archivio/${item.id}/${formato}`, {
        headers,
        params,
        responseType: "blob",
      });
      let fn = `fattura_passiva_${item.id}.${formato}`;
      const cd = res.headers["content-disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m) fn = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fn;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File scaricato");
    } catch (e) {
      let msg = e.response?.data?.detail || "Download non riuscito";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          if (j.detail) msg = j.detail;
        } catch { /* ignore */ }
      }
      toast.error(typeof msg === "string" ? msg : "Download non riuscito");
    } finally {
      setDownloadingArchivio(null);
    }
  };

  const loadFatturePassive = useCallback(
    async (pageNum) => {
      if (!hasProviderConfig) return;
      const page = pageNum ?? passivePage;
      setPassiveLoading(true);
      setPassiveMessage("");
      setPassiveSearched(true);
      try {
        const params = {
          page,
          per_page: 25,
          tipo_fic: passiveTipoFic,
        };
        if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
        if (passiveDataDa) params.data_da = passiveDataDa;
        if (passiveDataA) params.data_a = passiveDataA;
        const res = await axios.get(`${API_URL}/api/fatturazione/fatture-passive`, { headers, params });
        if (res.data.success === false) {
          setPassiveItems([]);
          setPassiveMessage(res.data.message || "Elenco non disponibile");
          setPassiveLastPage(1);
          setPassiveTotal(0);
          return;
        }
        setPassivePage(page);
        const items = res.data.items || [];
        setPassiveItems(items);
        setPassiveLastPage(res.data.last_page || 1);
        setPassiveTotal(res.data.total ?? items.length);
        if (res.data.message) setPassiveMessage(res.data.message);
      } catch (e) {
        setPassiveItems([]);
        setPassiveMessage(e.response?.data?.detail || e.message || "Errore caricamento fatture passive");
        setPassiveLastPage(1);
        setPassiveTotal(0);
      } finally {
        setPassiveLoading(false);
      }
    },
    [headers, hasProviderConfig, passivePage, passiveDataDa, passiveDataA, passiveTipoFic, strutturaIdForConfig],
  );

  const handleDownloadPassiva = async (item, formato = "xml") => {
    const docKey = item.fic_document_id ?? item.aruba_filename ?? "";
    const key = `${docKey}:${formato}`;
    setDownloadingPassive(key);
    try {
      const params = { formato };
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      if (item.fic_document_id != null) params.fic_document_id = item.fic_document_id;
      if (item.aruba_filename) params.aruba_filename = item.aruba_filename;
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture-passive/scarica`, {
        headers,
        params,
        responseType: "blob",
      });
      let fn = `fattura_passiva.${formato}`;
      const cd = res.headers["content-disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m) fn = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fn;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File scaricato");
    } catch (e) {
      let msg = e.response?.data?.detail || "Download non riuscito";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          if (j.detail) msg = j.detail;
        } catch { /* ignore */ }
      }
      toast.error(typeof msg === "string" ? msg : "Download non riuscito");
    } finally {
      setDownloadingPassive(null);
    }
  };

  const openDetail = useCallback(
    async (fatturaId) => {
      setDetailId(fatturaId);
      setDetailOpen(true);
      setDetailFattura(null);
      setDetailLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/fatturazione/fatture/dettaglio/${fatturaId}`,
          { headers, params: { include_xml: false } },
        );
        setDetailFattura(res.data.fattura || null);
      } catch {
        setDetailFattura(null);
        toast.error("Impossibile caricare il dettaglio della fattura");
      } finally {
        setDetailLoading(false);
      }
    },
    [headers],
  );

  const reloadDetail = useCallback(
    async (fatturaId) => {
      if (!fatturaId) return;
      setDetailLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/fatturazione/fatture/dettaglio/${fatturaId}`,
          { headers, params: { include_xml: false } },
        );
        setDetailFattura(res.data.fattura || null);
      } catch {
        /* mantieni il dettaglio già visibile se il refresh silenzioso fallisce */
      } finally {
        setDetailLoading(false);
      }
    },
    [headers],
  );

  const openRecuperoFic = useCallback(
    async (fatturaOrId) => {
      const id = typeof fatturaOrId === "object" ? fatturaOrId?.id : fatturaOrId;
      if (!id) return;
      let fatturaObj = typeof fatturaOrId === "object" ? fatturaOrId : null;
      if (!fatturaObj?.numero) {
        try {
          const res = await axios.get(`${API_URL}/api/fatturazione/fatture/dettaglio/${id}`, {
            headers,
            params: { include_xml: false },
          });
          fatturaObj = res.data.fattura || null;
        } catch {
          toast.error("Impossibile caricare la fattura per il recupero FIC");
          return;
        }
      }
      if (!fatturaObj) return;
      setRecuperoFicFattura(fatturaObj);
      setRecuperoFicOpen(true);
    },
    [headers],
  );

  const handleScollegaFic = useCallback(
    async (fattura) => {
      if (!fattura?.id) return;
      if (
        !window.confirm(
          `Scollegare ${fattura.numero || "questa fattura"} da Fatture in Cloud?\n` +
            "Il documento Affidya resta; potrai collegarne un altro o rigenerare la fattura del mese.",
        )
      ) {
        return;
      }
      try {
        const res = await axios.post(
          `${API_URL}/api/fatturazione/fatture/${fattura.id}/scollega-fic`,
          {},
          { headers },
        );
        toast.success(res.data?.message || "Collegamento FIC rimosso");
        await loadFatture({ silent: true });
        if (detailOpen && detailId === fattura.id) await openDetail(fattura.id);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Scollegamento FIC non riuscito");
      }
    },
    [headers, loadFatture, detailOpen, detailId, openDetail],
  );

  const refreshFatturazioneState = useCallback(
    async (fatturaId, { responseData, refreshDetail = true } = {}) => {
      if (fatturaId && responseData) {
        const optimisticPatch = {};
        if (responseData.stato) optimisticPatch.stato = responseData.stato;
        if (responseData.success) {
          optimisticPatch.sdi_invio_at = new Date().toISOString();
          optimisticPatch.ultimo_errore_sdi = undefined;
          optimisticPatch.ultimo_errore_sdi_leggibile = undefined;
        } else {
          if (responseData.message) optimisticPatch.ultimo_errore_sdi_leggibile = responseData.message;
          if (responseData.message_technical) optimisticPatch.ultimo_errore_sdi = responseData.message_technical;
        }
        pendingFatturaRefreshRef.current.set(fatturaId, {
          ts: Date.now(),
          patch: optimisticPatch,
        });

        setFatture((prev) =>
          prev.map((f) => {
            if (f.id !== fatturaId) return f;
            return { ...f, ...optimisticPatch };
          }),
        );
        if (responseData.stato) {
          patchControlloRigaFattura(fatturaId, { stato_fattura: responseData.stato });
        }
        if (detailOpen && detailId === fatturaId && detailFattura?.id === fatturaId) {
          setDetailFattura((prev) => (prev ? { ...prev, ...optimisticPatch } : prev));
        }
      }

      const reloads = [loadFatture({ silent: true }), reloadControlloCompleto()];
      if (refreshDetail && detailOpen && detailId && fatturaId && detailId === fatturaId) {
        reloads.push(reloadDetail(fatturaId));
      }
      await Promise.all(reloads);
    },
    [
      loadFatture,
      reloadControlloCompleto,
      detailOpen,
      detailId,
      detailFattura?.id,
      reloadDetail,
      patchControlloRigaFattura,
    ],
  );

  const handleRecuperoFicSuccess = useCallback(
    async (result) => {
      detailDirtyRef.current = true;
      await refreshFatturazioneState(result?.fattura_id, {
        responseData: {
          success: true,
          stato: result?.stato,
        },
      });
    },
    [refreshFatturazioneState],
  );

  const handleDetailOpenChange = (open) => {
    if (open) {
      setDetailOpen(true);
      return;
    }
    const closedId = detailId;
    const shouldRefresh = detailDirtyRef.current;
    detailDirtyRef.current = false;
    setDetailOpen(false);
    setDetailId(null);
    setDetailFattura(null);
    setEditVociDialogOpen(false);
    setOpeningDetailPdf(false);
    setShowSollecitoDialog(false);
    setSollecitoForm({ messaggio: "", email_destinatario: "" });
    if (shouldRefresh) {
      void refreshListsOnDetailClose(closedId);
    }
  };

  const handleOpenDetailFatturaPdf = useCallback(async () => {
    if (!detailFattura?.id) return;
    if (!(detailFattura.voci?.length > 0)) {
      toast.error("Nessuna voce: PDF non disponibile per questa fattura");
      return;
    }
    setOpeningDetailPdf(true);
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fattura/${detailFattura.id}/pdf`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (e) {
      let msg = "Impossibile aprire il PDF della fattura";
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const t = await data.text();
          const j = JSON.parse(t);
          if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        } catch {
          /* ignore */
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setOpeningDetailPdf(false);
    }
  }, [detailFattura, headers]);

  const handleOpenFatturaPdf = useCallback(
    async (fattura) => {
      const id = fattura?.id;
      if (!id) return;
      if (!(fattura?.voci?.length > 0)) {
        toast.error("PDF non disponibile per questa fattura");
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/fatturazione/fattura/${id}/pdf`, {
          headers,
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 120_000);
      } catch (e) {
        let msg = "Impossibile aprire il PDF";
        const data = e.response?.data;
        if (data instanceof Blob) {
          try {
            const t = await data.text();
            const j = JSON.parse(t);
            if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
          } catch {
            /* ignore */
          }
        } else if (e.response?.data?.detail) {
          msg = e.response.data.detail;
        }
        toast.error(msg);
      }
    },
    [headers],
  );

  const handleInviaSollecito = async () => {
    if (!detailFattura?.id) return;
    setSendingSollecito(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${detailFattura.id}/sollecito`,
        sollecitoForm,
        { headers },
      );
      toast.success(res.data.message || "Sollecito registrato");
      setShowSollecitoDialog(false);
      setSollecitoForm({ messaggio: "", email_destinatario: "" });
      await openDetail(detailFattura.id);
      loadFatture();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio sollecito");
    } finally {
      setSendingSollecito(false);
    }
  };

  const handleDownloadXml = async (fatturaId, numero) => {
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture/${fatturaId}/xml`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${String(numero || "fattura").replace(/\//g, "_")}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("XML scaricato");
    } catch {
      toast.error("Download XML non riuscito");
    }
  };

  const handleSincronizzaSdi = useCallback(async () => {
    setSdiSyncLoading(true);
    try {
      const vr = await axios.post(`${API_URL}/api/fatturazione/sync-sdi-status`, {}, { headers });
      const updated = vr.data?.updated ?? 0;
      const refreshed = vr.data?.refreshed ?? 0;
      const checked = vr.data?.checked ?? 0;
      const errors = vr.data?.errors ?? 0;
      const usageLimit = !!vr.data?.usage_limit || !!vr.data?.sospeso;
      const errorDetails = vr.data?.error_details || [];
      await loadFatture();
      if (usageLimit) {
        const msg = vr.data?.message || FIC_RATE_LIMIT_MESSAGE;
        setSdiUsageLimitBanner({ ...vr.data, message: msg });
        toast.warning(msg);
      } else {
        if (vr.data?.in_coda) toast.success(vr.data?.message || "Aggiornamento programmato");
        if (updated > 0) {
          toast.success(`Stato SDI aggiornato per ${updated} fattur${updated === 1 ? "a" : "e"} (${checked} controllate)`);
        } else if (refreshed > 0) {
          toast.success(`Stato FIC sincronizzato su ${refreshed} fattur${refreshed === 1 ? "a" : "e"} (ancora in consegna SDI)`);
        } else if (errors > 0) {
          const first = errorDetails[0];
          const hint = first?.numero ? `${first.numero}: ${first.message}` : `${errors} errori di sincronizzazione`;
          toast.warning(`Controllate ${checked}, ${errors} errori. ${hint}`);
        } else if (checked > 0) {
          toast.success(`${checked} fatture controllate: stato SDI invariato (probabilmente ancora in consegna)`);
        } else {
          toast.success("Nessuna fattura in attesa da sincronizzare");
        }
      }
    } catch {
      toast.error("Sincronizzazione SDI non riuscita");
    } finally {
      setSdiSyncLoading(false);
    }
  }, [loadFatture, headers]);

  const dismissSdiUsageLimitBanner = useCallback(() => setSdiUsageLimitBanner(null), []);

  const openSollecitoForFattura = useCallback(
    async (fatturaOrId) => {
      const id = typeof fatturaOrId === "object" ? fatturaOrId?.id : fatturaOrId;
      if (!id) return;
      await openDetail(id);
      setSollecitoForm({ messaggio: "", email_destinatario: "" });
      setShowSollecitoDialog(true);
    },
    [openDetail],
  );

  const handleInviaSdi = async (fatturaId, { applicaSedeFic = false } = {}) => {
    if (!applicaSedeFic) setSendingSdi(true);
    else setConfirmingFicSede(true);
    try {
      const res = await postInviaSdi(axios, API_URL, fatturaId, {
        headers,
        strutturaId: strutturaIdForConfig,
        applicaSedeFic,
      });
      if (res.data?.success) {
        setFicSedeConfirm(null);
        toast.success(res.data.message || "Inviata a SDI");
      } else {
        toastSdiError(res.data?.message_technical || res.data?.message, {
          userMessage: res.data?.message,
        });
      }
      detailDirtyRef.current = true;
      await refreshFatturazioneState(fatturaId, { responseData: res.data });
    } catch (e) {
      if (!applicaSedeFic && isFicSedePropostaError(e)) {
        const proposta = ficSedePropostaFromError(e);
        if (proposta) {
          setFicSedeConfirm(proposta);
          return;
        }
      }
      const d = e.response?.data;
      toastSdiError(d?.message_technical || d?.detail || d?.message, {
        userMessage: d?.message || (typeof d?.detail === "string" ? d.detail : undefined),
      });
      detailDirtyRef.current = true;
      await refreshFatturazioneState(fatturaId, { refreshDetail: true });
    } finally {
      if (!applicaSedeFic) setSendingSdi(false);
      else setConfirmingFicSede(false);
    }
  };

  const handleConfermaSedeFicInvio = () => {
    if (!ficSedeConfirm?.fatturaId) return;
    handleInviaSdi(ficSedeConfirm.fatturaId, { applicaSedeFic: true });
  };

  const handleCreaNotaCredito = useCallback(async (f) => {
    if (!f?.id) return;
    setCreatingNotaCredito(f.id);
    try {
      const res = await axios.post(`${API_URL}/api/fatturazione/fatture/${f.id}/nota-credito`, {}, { headers });
      toast.success(res.data?.message || "Nota di credito creata");
      await loadFatture();
      handleTabChange("note-credito");
      if (res.data?.fattura_id) {
        await openDetail(res.data.fattura_id);
      } else if (detailOpen && detailId === f.id) {
        await openDetail(f.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Creazione nota di credito non riuscita");
    } finally {
      setCreatingNotaCredito(null);
    }
  }, [headers, loadFatture, detailOpen, detailId, openDetail, handleTabChange]);

  const handleRigeneraFattura = useCallback(async (f) => {
    if (!f?.id) return;
    setRigeneratingFattura(f.id);
    try {
      const res = await axios.post(`${API_URL}/api/fatturazione/fatture/${f.id}/rigenera`, {}, { headers });
      toast.success(res.data?.message || "Fattura rigenerata");
      await loadFatture();
      if (detailOpen && detailId === f.id) await openDetail(f.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rigenerazione non riuscita");
    } finally {
      setRigeneratingFattura(null);
    }
  }, [headers, loadFatture, detailOpen, detailId, openDetail]);

  const handleApprovaBozza = useCallback(async () => {
    if (!detailFattura?.id || detailFattura.stato !== "bozza") return;
    const mese = detailFattura.mese;
    if (!mese) {
      toast.error("Mese competenza mancante sulla fattura");
      return;
    }
    setApprovingBozza(true);
    try {
      const body = { mese, fattura_ids: [detailFattura.id] };
      if (strutturaIdForConfig) body.strutturaId = strutturaIdForConfig;
      const res = await axios.post(
        `${API_URL}/api/fatturazione/controllo-mensile/approva`,
        body,
        { headers },
      );
      toast.success(res.data?.message || "Fattura approvata");
      if (res.data?.errori?.length) {
        res.data.errori.slice(0, 3).forEach((e) => toast.warning(String(e)));
      }
      await openDetail(detailFattura.id);
      loadFatture();
      loadControllo();
    } catch (e) {
      const d = e.response?.data;
      toast.error(typeof d?.detail === "string" ? d.detail : d?.message || "Approvazione non riuscita");
    } finally {
      setApprovingBozza(false);
    }
  }, [detailFattura, headers, openDetail, loadFatture, loadControllo, strutturaIdForConfig]);

  const handleRicalcolaFatturaSenzaBollo = async () => {
    if (!detailFattura) return;
    setSavingFatturaPatch(true);
    try {
      await axios.patch(`${API_URL}/api/fatturazione/fattura/${detailFattura.id}`, {}, { headers });
      toast.success("Fattura aggiornata (totali senza bollo, XML rigenerato)");
      await openDetail(detailFattura.id);
      loadFatture();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aggiornamento non riuscito");
    } finally {
      setSavingFatturaPatch(false);
    }
  };

  const openEditVociDialog = () => {
    if (!detailFattura?.voci?.length) {
      toast.error("Nessuna voce da modificare");
      return;
    }
    setEditVociDialogOpen(true);
  };

  const handleSaveVociFromEditor = useCallback(
    async (voci) => {
      if (!detailFattura) return;
      setSavingFatturaPatch(true);
      try {
        await axios.patch(
          `${API_URL}/api/fatturazione/fattura/${detailFattura.id}`,
          { voci },
          { headers },
        );
        toast.success("Voci aggiornate");
        setEditVociDialogOpen(false);
        await openDetail(detailFattura.id);
        loadFatture();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
      } finally {
        setSavingFatturaPatch(false);
      }
    },
    [detailFattura, headers, openDetail, loadFatture],
  );

  const confirmDeleteFattura = async () => {
    if (!deleteTarget) return;
    setDeletingFattura(true);
    try {
      const params = {};
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      if (deleteTarget.eliminaFic) params.elimina_fic = true;
      const res = await axios.delete(`${API_URL}/api/fatturazione/fattura/${deleteTarget.id}`, {
        headers,
        params,
      });
      toast.success(res.data?.message || "Fattura eliminata. Puoi generarne una nuova dal fascicolo.");
      if (detailId === deleteTarget.id) {
        handleDetailOpenChange(false);
      }
      setDeleteTarget(null);
      loadFatture();
      loadControllo();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione non riuscita");
    } finally {
      setDeletingFattura(false);
    }
  };

  const confirmBulkDeleteFatture = async () => {
    const targets = bulkDeleteTargets || [];
    if (!targets.length) return;
    setDeletingFattura(true);
    let ok = 0;
    const errori = [];
    try {
      for (const t of targets) {
        try {
          const params = {};
          if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
          if (t.eliminaFic) params.elimina_fic = true;
          await axios.delete(`${API_URL}/api/fatturazione/fattura/${t.id}`, { headers, params });
          ok += 1;
          if (detailId === t.id) handleDetailOpenChange(false);
        } catch (e) {
          errori.push(`${t.numero || t.id}: ${e.response?.data?.detail || "errore"}`);
        }
      }
      if (ok) {
        toast.success(
          ok === 1 ? "1 fattura eliminata" : `${ok} fatture eliminate`,
        );
      }
      if (errori.length) {
        toast.error(
          errori.length <= 3
            ? errori.join(" · ")
            : `${errori.length} eliminazioni non riuscite (es. ${errori[0]})`,
        );
      }
      setBulkDeleteTargets(null);
      loadFatture();
      loadControllo();
    } finally {
      setDeletingFattura(false);
    }
  };

  const confirmDeleteAttivaFic = async () => {
    if (!deleteAttivaTarget?.fic_document_id) return;
    setDeletingAttivaFic(true);
    try {
      const params = {};
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.delete(
        `${API_URL}/api/fatturazione/fatture-attive/${deleteAttivaTarget.fic_document_id}`,
        { headers, params },
      );
      toast.success(res.data?.message || "Fattura eliminata su FIC");
      setDeleteAttivaTarget(null);
      loadFattureAttive(attivePage);
      loadFatture();
      loadControllo();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione FIC non riuscita");
    } finally {
      setDeletingAttivaFic(false);
    }
  };

  const buildDeleteTarget = useCallback(
    (f) => ({
      id: f.id,
      numero: f.numero,
      provider_id: f.provider_id || f.fic_document_id,
      sdi_ei_status: f.sdi_ei_status,
      eliminaFic: fatturaEliminaSuFic(f, isFic),
    }),
    [isFic],
  );

  useEffect(() => {
    if (!emailOk || !token) return;
    loadFascicoli();
  }, [emailOk, token, loadFascicoli, activeStruttura?.id]);

  useEffect(() => {
    if (!emailOk || !token) return;
    loadFatture();
  }, [emailOk, token, loadFatture, activeStruttura?.id]);

  useEffect(() => {
    if (!emailOk || !token) return;
    loadControllo();
  }, [emailOk, token, loadControllo, activeStruttura?.id]);

  useEffect(() => {
    if (!emailOk || !token) return;
    loadFatturazioneConfig();
  }, [emailOk, token, loadFatturazioneConfig, activeStruttura?.id]);

  useEffect(() => {
    if (!emailOk || !token) return;
    loadArchivioStats();
  }, [emailOk, token, loadArchivioStats, activeStruttura?.id]);

  // Ricarica archivio passive all'apertura del tab / cambio mese o struttura
  useEffect(() => {
    if (activeTab === "fatture-passive" && emailOk && token) {
      loadArchivioPassive(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evita reload ad ogni keystroke nei filtri testo
  }, [activeTab, emailOk, token, strutturaIdForConfig, archivioFiltroMese]);

  if (!emailOk) {
    return <Navigate to="/" replace />;
  }

  const strutturaLabel = activeStruttura?.name || user?.facility_name || "Struttura";

  return (
    <div className="p-4 md:p-5 lg:p-6 w-full min-w-0 max-w-none space-y-6">
      <PageHeader
        title="Fatturazione"
        subtitle="Gestione completa fatture e incassi della struttura."
        icon={Receipt}
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs">
                  <Settings className="w-3.5 h-3.5 mr-1.5" />
                  Configurazione
                  <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/fatturazione/modelli")}>
                  <FileText className="w-4 h-4 mr-2" />
                  Modelli fattura
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/fatturazione/aspetto")}>
                  <Receipt className="w-4 h-4 mr-2" />
                  Aspetto fattura
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/fatturazione/modelli")}>
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Variabili dinamiche
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/impostazioni")}>
                  <Cloud className="w-4 h-4 mr-2" />
                  Sincronizzazione Fatture in Cloud
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <FatturazioneTabsContent
        headers={headers}
        strutturaLabel={strutturaLabel}
        user={user}
        currentYear={currentYear}
        formatEuro={formatEuro}
        formatMese={formatMese}
        formatDataIt={formatDataIt}
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        unifiedKpis={unifiedKpis}
        kpiConfrontoMensile={kpiConfrontoMensile}
        fattureGenerateStats={fattureGenerateStats}
        archivioTotal={archivioTotal}
        fattureSdiStats={fattureSdiStats}
        navigate={navigate}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filtroStato={filtroStato}
        setFiltroStato={setFiltroStato}
        filtroMese={filtroMese}
        setFiltroMese={setFiltroMese}
        filtroFascicolo={filtroFascicolo}
        setFiltroFascicolo={setFiltroFascicolo}
        fascicoli={fascicoli}
        loadingFascicoli={loadingFascicoli}
        mesiOptions={mesiOptions}
        FILTRO_STATO_UI={FILTRO_STATO_UI}
        loadingFatture={loadingFatture}
        loadFatture={loadFatture}
        fattureFiltrate={fattureFiltrate}
        fatturePaginate={fatturePaginate}
        tablePage={tablePage}
        setTablePage={setTablePage}
        tableLastPage={tableLastPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        STATO_BADGE={STATO_BADGE}
        fascicoloLabel={(fid) => fascicoloLabel(fascicoli, fid)}
        openDetail={openDetail}
        openRecuperoFic={openRecuperoFic}
        handleInviaSdi={handleInviaSdi}
        sendingSdi={sendingSdi}
        canInviaSdi={canInviaSdi}
        handleDownloadXml={handleDownloadXml}
        handleOpenFatturaPdf={handleOpenFatturaPdf}
        handleCreaNotaCredito={handleCreaNotaCredito}
        handleRigeneraFattura={handleRigeneraFattura}
        creatingNotaCredito={creatingNotaCredito}
        rigeneratingFattura={rigeneratingFattura}
        fatturaModificabileEliminabile={(f) => fatturaEliminabile(f, { isFic })}
        buildDeleteTarget={buildDeleteTarget}
        setDeleteTarget={setDeleteTarget}
        onBulkDeleteRequest={(targets) => setBulkDeleteTargets(targets)}
        bulkDeleting={deletingFattura && !!bulkDeleteTargets}
        formatDataOraIt={formatDataOraIt}
        sdiStats={sdiStats}
        sdiSearchQuery={sdiSearchQuery}
        setSdiSearchQuery={setSdiSearchQuery}
        sdiFiltroStato={sdiFiltroStato}
        setSdiFiltroStato={setSdiFiltroStato}
        sdiFiltroMese={sdiFiltroMese}
        setSdiFiltroMese={setSdiFiltroMese}
        sdiDataDa={sdiDataDa}
        setSdiDataDa={setSdiDataDa}
        sdiDataA={sdiDataA}
        setSdiDataA={setSdiDataA}
        sdiMesiOptions={sdiMesiOptions}
        clearSdiFilters={clearSdiFilters}
        handleSincronizzaSdi={handleSincronizzaSdi}
        sdiSyncLoading={sdiSyncLoading}
        sdiUsageLimitBanner={sdiUsageLimitBanner}
        dismissSdiUsageLimitBanner={dismissSdiUsageLimitBanner}
        fattureSdiFiltrate={fattureSdiFiltrate}
        fattureSdiPaginate={fattureSdiPaginate}
        sdiPage={sdiPage}
        setSdiPage={setSdiPage}
        sdiLastPage={sdiLastPage}
        sdiRowsPerPage={sdiRowsPerPage}
        setSdiRowsPerPage={setSdiRowsPerPage}
        onSollecitaPagamento={openSollecitoForFattura}
        hasProviderConfig={hasProviderConfig}
        isFic={isFic}
        isAruba={isAruba}
        fattConfig={fattConfig}
        archivioLoading={archivioLoading}
        archivioLoaded={archivioLoaded}
        archivioItems={archivioItems}
        archivioStats={archivioStats}
        archivioStatsLoading={archivioStatsLoading}
        archivioFiltroPagamento={archivioFiltroPagamento}
        setArchivioFiltroPagamento={setArchivioFiltroPagamento}
        archivioFiltroMese={archivioFiltroMese}
        setArchivioFiltroMese={setArchivioFiltroMese}
        archivioSearchQuery={archivioSearchQuery}
        setArchivioSearchQuery={setArchivioSearchQuery}
        archivioMesiOptions={archivioMesiOptions}
        archivioTotalCount={archivioTotal}
        TIPO_PASSIVA_LABELS={TIPO_PASSIVA_LABELS}
        STATO_PAGAMENTO_PASSIVA_UI={STATO_PAGAMENTO_PASSIVA_UI}
        handleOpenArchivioPdf={handleOpenArchivioPdf}
        clearArchivioFilters={() => {
          setArchivioSearchQuery("");
          setArchivioFornitore("");
          setArchivioFiltroPagamento("__tutti__");
          setArchivioFiltroMese(currentMonthValue);
          setArchivioDataDa("");
          setArchivioDataA("");
        }}
        archivioDataDa={archivioDataDa}
        setArchivioDataDa={setArchivioDataDa}
        archivioDataA={archivioDataA}
        setArchivioDataA={setArchivioDataA}
        archivioFornitore={archivioFornitore}
        setArchivioFornitore={setArchivioFornitore}
        loadArchivioPassive={loadArchivioPassive}
        archivioPage={archivioPage}
        archivioLastPage={archivioLastPage}
        handleDownloadArchivio={handleDownloadArchivio}
        downloadingArchivio={downloadingArchivio}
        openPassivePayDialog={openPassivePayDialog}
        syncLoading={syncLoading}
        syncDataDa={syncDataDa}
        setSyncDataDa={setSyncDataDa}
        syncDataA={syncDataA}
        setSyncDataA={setSyncDataA}
        syncTipoFic={syncTipoFic}
        setSyncTipoFic={setSyncTipoFic}
        handleSincronizzaPassive={handleSincronizzaPassive}
        syncResult={syncResult}
        passiveDataDa={passiveDataDa}
        setPassiveDataDa={setPassiveDataDa}
        passiveDataA={passiveDataA}
        setPassiveDataA={setPassiveDataA}
        passiveTipoFic={passiveTipoFic}
        setPassiveTipoFic={setPassiveTipoFic}
        loadFatturePassive={loadFatturePassive}
        passiveLoading={passiveLoading}
        passiveMessage={passiveMessage}
        passiveSearched={passiveSearched}
        passiveItems={passiveItems}
        handleDownloadPassiva={handleDownloadPassiva}
        downloadingPassive={downloadingPassive}
        passivePage={passivePage}
        passiveLastPage={passiveLastPage}
        passiveTotal={passiveTotal}
        attiveFiltroMese={attiveFiltroMese}
        setAttiveFiltroMese={setAttiveFiltroMese}
        attiveMesiOptions={mesiOptions}
        attiveItems={attiveItems}
        attiveLoading={attiveLoading}
        attiveTotal={attiveTotal}
        attiveImportoTotale={attiveStats?.importoTotale}
        attivePage={attivePage}
        attiveLastPage={attiveLastPage}
        setAttivePage={setAttivePage}
        attiveSearchQuery={attiveSearchQuery}
        setAttiveSearchQuery={setAttiveSearchQuery}
        loadFattureAttive={loadFattureAttive}
        handleSyncFicNoteCredito={handleSyncFicNoteCredito}
        syncFicNcLoading={syncFicNcLoading}
        handleSyncPagamentiFic={handleSyncPagamentiFic}
        syncPagamentiLoading={syncPagamentiLoading}
        handleOpenAttivaPdf={handleOpenAttivaPdf}
        downloadingAttivaPdf={downloadingAttivaPdf}
        deleteAttivaTarget={deleteAttivaTarget}
        setDeleteAttivaTarget={setDeleteAttivaTarget}
        noteCredito={noteCredito}
        fattureEligibiliNc={fattureEligibiliNc}
        ncSearchQuery={ncSearchQuery}
        setNcSearchQuery={setNcSearchQuery}
        ncFiltroMese={ncFiltroMese}
        setNcFiltroMese={setNcFiltroMese}
        meseControllo={meseControllo}
        setMeseControllo={setMeseControllo}
        controlloData={controlloData}
        controlloLoading={controlloLoading}
        loadControllo={loadControllo}
        registerControlloFullRefresh={registerControlloFullRefresh}
        strutturaIdForConfig={strutturaIdForConfig}
      />

      <Dialog open={!!passivePayTarget} onOpenChange={(open) => !open && setPassivePayTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-left">Fattura passiva</DialogTitle>
            <DialogDescription className="text-left">
              {passivePayTarget?.fornitore} — {passivePayTarget?.numero || "senza numero"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="text-xs text-slate-500">Stato pagamento</Label>
              <Select
                value={passivePayForm.stato_pagamento}
                onValueChange={(v) => setPassivePayForm((p) => ({ ...p, stato_pagamento: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="non_pagata">Da pagare</SelectItem>
                  <SelectItem value="pagata">Pagata</SelectItem>
                  <SelectItem value="parziale">Parzialmente pagata</SelectItem>
                  <SelectItem value="scaduta">Scaduta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data scadenza</Label>
              <Input
                type="date"
                className="mt-1"
                value={passivePayForm.data_scadenza}
                onChange={(e) => setPassivePayForm((p) => ({ ...p, data_scadenza: e.target.value }))}
              />
            </div>
            {passivePayForm.stato_pagamento === "parziale" && (
              <div>
                <Label className="text-xs text-slate-500">Importo già pagato (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1"
                  value={passivePayForm.importo_pagato}
                  onChange={(e) => setPassivePayForm((p) => ({ ...p, importo_pagato: e.target.value }))}
                />
              </div>
            )}
            {["pagata", "parziale"].includes(passivePayForm.stato_pagamento) && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Data pagamento</Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={passivePayForm.data_pagamento}
                    onChange={(e) => setPassivePayForm((p) => ({ ...p, data_pagamento: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Metodo pagamento</Label>
                  <Select
                    value={passivePayForm.metodo_pagamento}
                    onValueChange={(v) => setPassivePayForm((p) => ({ ...p, metodo_pagamento: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bonifico">Bonifico</SelectItem>
                      <SelectItem value="rid">RID</SelectItem>
                      <SelectItem value="carta">Carta</SelectItem>
                      <SelectItem value="contanti">Contanti</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label className="text-xs text-slate-500">Centro di costo (facoltativo)</Label>
              <Input
                className="mt-1"
                placeholder="Es. Reparto, unità…"
                value={passivePayForm.centro_costo}
                onChange={(e) => setPassivePayForm((p) => ({ ...p, centro_costo: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Categoria</Label>
              <Select
                value={passivePayForm.categoria || "__nessuna__"}
                onValueChange={(v) =>
                  setPassivePayForm((p) => ({ ...p, categoria: v === "__nessuna__" ? "" : v }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__nessuna__">— Nessuna —</SelectItem>
                  {CATEGORIE_PASSIVA.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPassivePayTarget(null)} disabled={savingPassivePay}>
              Annulla
            </Button>
            <Button type="button" onClick={handleSavePagamentoPassiva} disabled={savingPassivePay} className="bg-emerald-600 hover:bg-emerald-700">
              {savingPassivePay ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={handleDetailOpenChange}>
        <DialogContent
          className="!flex max-w-lg sm:max-w-2xl max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:w-full"
          data-testid="fattura-detail-dialog"
        >
          <DialogHeader className="shrink-0 border-b border-slate-100 px-6 pb-3 pt-6 text-left">
            <DialogTitle className="flex items-center gap-2 pr-8 text-left">
              <Receipt className="w-5 h-5 shrink-0 text-emerald-600" />
              <span className="truncate">
                {detailFattura?.numero ? `Fattura ${detailFattura.numero}` : "Dettaglio fattura"}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 py-3">
            {detailLoading && (
              <div className="flex flex-col items-center py-12 text-slate-500">
                <Loader2 className="mb-2 h-8 w-8 animate-spin" />
                Caricamento…
              </div>
            )}
            {!detailLoading && detailFattura && (
              <FatturaDetailBody
                f={detailFattura}
                formatDataIt={formatDataIt}
                formatDataOraIt={formatDataOraIt}
                headers={headers}
                canEditAllegati={fatturaModificabile(detailFattura)}
                onAllegatiChanged={() => openDetail(detailFattura.id)}
                ospiteNome={fascicoloLabel(fascicoli, detailFattura.fascicolo_id) || ""}
              />
            )}
            {!detailLoading && !detailFattura && detailId && (
              <p className="py-8 text-center text-sm text-slate-500">Dettaglio non disponibile.</p>
            )}
          </div>
          {detailFattura && (
            <DialogFooter className="px-6 py-4 border-t border-slate-200 shrink-0 flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:justify-between sm:items-center">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-violet-200 text-violet-800"
                  onClick={handleOpenDetailFatturaPdf}
                  disabled={openingDetailPdf || !(detailFattura.voci?.length > 0)}
                  title={
                    !(detailFattura.voci?.length > 0)
                      ? "PDF non disponibile senza voci"
                      : "Genera e apri il PDF (copia di cortesia) in una nuova scheda"
                  }
                >
                  {openingDetailPdf ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-1" />
                  )}
                  Apri PDF
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadXml(detailFattura.id, detailFattura.numero)}
                  disabled={!detailFattura.xml}
                  title={!detailFattura.xml ? "XML disponibile dopo l'approvazione" : undefined}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Scarica XML
                </Button>
                {detailFattura.stato === "bozza" && (
                  <Button
                    type="button"
                    size="sm"
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={handleApprovaBozza}
                    disabled={
                      approvingBozza ||
                      savingFatturaPatch ||
                      detailProblemiControllo.length > 0 ||
                      !hasProviderConfig
                    }
                    title={
                      !hasProviderConfig
                        ? "Configura la fatturazione in Impostazioni"
                        : detailProblemiControllo.length > 0
                          ? detailProblemiControllo.join(" · ")
                          : "Genera l'XML e conferma la fattura per l'invio SDI"
                    }
                  >
                    {approvingBozza ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                    )}
                    Approva bozza
                  </Button>
                )}
                {["generata", "approvata", "rifiutata", "scartata"].includes(detailFattura.stato) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-700"
                    onClick={() => handleInviaSdi(detailFattura.id)}
                    disabled={sendingSdi || !canInviaSdi || fatturaInCodaSdi(detailFattura)}
                    title={
                      fatturaInCodaSdi(detailFattura)
                        ? "Fattura già in coda per l'invio a SDI"
                        : !canInviaSdi
                          ? "Configura e abilita la fatturazione in Impostazioni"
                          : undefined
                    }
                  >
                    {sendingSdi || fatturaInCodaSdi(detailFattura) ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-1" />
                    )}
                    {fatturaInCodaSdi(detailFattura) ? "In coda SDI…" : "Invia a SDI"}
                  </Button>
                )}
                {isFic && fatturaRecuperabileCollegamentoFic(detailFattura) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-violet-200 text-violet-800 hover:bg-violet-50"
                    onClick={() => openRecuperoFic(detailFattura)}
                    title="Cerca su Fatture in Cloud e collega il documento esistente"
                  >
                    <Link2 className="w-4 h-4 mr-1" />
                    Recupera da FIC
                  </Button>
                )}
                {isFic && (detailFattura.provider_id || detailFattura.fic_document_id) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-amber-200 text-amber-800 hover:bg-amber-50"
                    onClick={() => handleScollegaFic(detailFattura)}
                    title="Rimuove il collegamento al documento FIC (utile dopo NC o per ricollegare)"
                  >
                    <Unlink className="w-4 h-4 mr-1" />
                    Scollega FIC
                  </Button>
                )}
                {detailFattura.tipo_documento !== "nota_credito" &&
                  ["inviata_sdi", "consegnata", "accettata"].includes(detailFattura.stato) &&
                  !detailFattura.nota_credito_id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-purple-200 text-purple-800"
                      onClick={() => handleCreaNotaCredito(detailFattura)}
                      disabled={creatingNotaCredito === detailFattura.id}
                    >
                      {creatingNotaCredito === detailFattura.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4 mr-1" />
                      )}
                      Emetti NC
                    </Button>
                  )}
                {detailFattura.tipo_documento !== "nota_credito" && detailFattura.stato === "stornata" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-800"
                    onClick={() => handleRigeneraFattura(detailFattura)}
                    disabled={rigeneratingFattura === detailFattura.id}
                  >
                    {rigeneratingFattura === detailFattura.id ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    Rigenera fattura
                  </Button>
                )}
                {detailFattura.stato_pagamento !== "pagata" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-amber-200 text-amber-800 bg-amber-50/50 hover:bg-amber-50"
                    onClick={() => {
                      setSollecitoForm({ messaggio: "", email_destinatario: "" });
                      setShowSollecitoDialog(true);
                    }}
                  >
                    <Bell className="w-4 h-4 mr-1" />
                    Sollecita pagamento
                  </Button>
                )}
                {["scartata", "rifiutata"].includes(detailFattura.stato) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-emerald-200 text-emerald-700"
                    onClick={() => handleInviaSdi(detailFattura.id)}
                    disabled={sendingSdi || !canInviaSdi}
                    title={!canInviaSdi ? "Configura e abilita la fatturazione in Impostazioni" : undefined}
                  >
                    {sendingSdi ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Reinvia a SDI
                  </Button>
                )}
                {fatturaModificabile(detailFattura) && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRicalcolaFatturaSenzaBollo}
                      disabled={savingFatturaPatch}
                      title="Ricalcola imponibile, IVA e totale senza bollo e rigenera l'XML"
                    >
                      {savingFatturaPatch ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                      Ricalcola totali
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openEditVociDialog}
                      disabled={savingFatturaPatch}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Modifica voci
                    </Button>
                  </>
                )}
                {fatturaEliminabile(detailFattura, { isFic }) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    onClick={() => setDeleteTarget(buildDeleteTarget(detailFattura))}
                    disabled={savingFatturaPatch || deletingFattura}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {fatturaEliminaSuFic(detailFattura, isFic) ? "Elimina su FIC" : "Elimina"}
                  </Button>
                )}
              </div>
              {detailFattura.fascicolo_id && (
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link to={`/fascicoli-assistenziali/${detailFattura.fascicolo_id}?tab=amministrativa`}>
                    Apri fascicolo
                    <ExternalLink className="w-3.5 h-3.5 ml-1" />
                  </Link>
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showSollecitoDialog}
        onOpenChange={(open) => {
          setShowSollecitoDialog(open);
          if (!open) setSollecitoForm({ messaggio: "", email_destinatario: "" });
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <Bell className="h-5 w-5 text-amber-500" />
              Sollecita pagamento
            </DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-500">
              Invio automatico via email (PEC e WhatsApp in arrivo). Il messaggio fa riferimento alla fattura
              {detailFattura?.numero ? ` ${detailFattura.numero}` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Email destinatario</Label>
              <Input
                type="email"
                value={sollecitoForm.email_destinatario}
                onChange={(e) => setSollecitoForm({ ...sollecitoForm, email_destinatario: e.target.value })}
                className="mt-1"
                placeholder="Lascia vuoto per usare contatto familiare"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Messaggio</Label>
              <Textarea
                value={sollecitoForm.messaggio}
                onChange={(e) => setSollecitoForm({ ...sollecitoForm, messaggio: e.target.value })}
                className="mt-1"
                rows={3}
                placeholder="Messaggio personalizzato (opzionale)"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSollecitoDialog(false)}
              disabled={sendingSollecito}
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleInviaSollecito}
              disabled={sendingSollecito}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {sendingSollecito ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Invia sollecito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              {deleteTarget?.eliminaFic ? "Eliminare su FIC?" : "Eliminare la fattura?"}
            </DialogTitle>
            <DialogDescription className="text-left">
              <span className="font-mono text-slate-800">{deleteTarget?.numero}</span>
              {" — "}operazione definitiva.
              {deleteTarget?.eliminaFic ? (
                <>
                  {" "}Il documento verrà eliminato su Fatture in Cloud e il record collegato in Affidya.
                  Non è possibile se risultano pagamenti registrati o se la fattura è già stata inviata allo SDI.
                </>
              ) : (
                <>
                  {" "}Le righe collegate in Prima Nota torneranno disponibili per una nuova emissione.
                  Non è possibile se risultano pagamenti registrati o se la fattura è già stata inviata/accettata dal SDI.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)} disabled={deletingFattura}>
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteFattura}
              disabled={deletingFattura}
            >
              {deletingFattura ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {deleteTarget?.eliminaFic ? "Elimina su FIC" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!bulkDeleteTargets?.length}
        onOpenChange={(open) => !open && !deletingFattura && setBulkDeleteTargets(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              Eliminare {bulkDeleteTargets?.length || 0} fatture?
            </DialogTitle>
            <DialogDescription className="text-left">
              Operazione definitiva sulle fatture selezionate (bozze / non ancora inviate a SDI).
              Le righe Prima Nota torneranno disponibili. Le fatture già inviate allo SDI non sono
              selezionabili.
              {(bulkDeleteTargets || []).some((t) => t.eliminaFic) && (
                <> Alcuni documenti collegati verranno eliminati anche su Fatture in Cloud.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkDeleteTargets(null)}
              disabled={deletingFattura}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmBulkDeleteFatture}
              disabled={deletingFattura}
            >
              {deletingFattura ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Elimina selezionate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteAttivaTarget} onOpenChange={(open) => !open && setDeleteAttivaTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              Eliminare su FIC?
            </DialogTitle>
            <DialogDescription className="text-left">
              FIC n.{" "}
              <span className="font-mono text-slate-800">{deleteAttivaTarget?.numero}</span>
              {" — "}operazione definitiva. Il documento verrà eliminato su Fatture in Cloud
              {deleteAttivaTarget?.gia_collegata ? " e il record collegato in Affidya" : ""}.
              Disponibile solo per fatture non ancora inviate allo SDI e senza pagamenti registrati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteAttivaTarget(null)} disabled={deletingAttivaFic}>
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteAttivaFic}
              disabled={deletingAttivaFic}
            >
              {deletingAttivaFic ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Elimina su FIC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModificaVociFatturaDialog
        open={editVociDialogOpen}
        onOpenChange={setEditVociDialogOpen}
        initialVoci={detailFattura?.voci}
        saving={savingFatturaPatch}
        onSave={handleSaveVociFromEditor}
      />

      <ConfermaSedeFicDialog
        open={!!ficSedeConfirm}
        onOpenChange={(open) => !open && setFicSedeConfirm(null)}
        ficSede={ficSedeConfirm?.ficSede}
        numeroFattura={ficSedeConfirm?.numero}
        loading={confirmingFicSede}
        onConfirm={handleConfermaSedeFicInvio}
      />

      <RecuperoCollegamentoFicDialog
        open={recuperoFicOpen}
        onOpenChange={(open) => {
          setRecuperoFicOpen(open);
          if (!open) setRecuperoFicFattura(null);
        }}
        headers={headers}
        fattura={recuperoFicFattura}
        strutturaIdForConfig={strutturaIdForConfig}
        onSuccess={handleRecuperoFicSuccess}
      />
    </div>
  );
}
